import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './Services.css';
import LanguageContext from './LanguageContext';
import { servicesPageCopy, translateService, typeTranslations } from './translations';
import { formatPrice } from './utils/priceFormatter';

function Services() {
  const { language } = useContext(LanguageContext);
  const copy = servicesPageCopy[language] || servicesPageCopy.English;
  const [activeFilter, setActiveFilter] = useState('All');
  const [services, setServices] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
    fetchSpecialists();
    fetchTrainers();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('services')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setServices(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(copy.error);
    }
  };

  const fetchSpecialists = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('specialists')
        .select('*')
        .eq('is_active', true)
        // Order by display_order (sequence set in admin) - matches AdminSpecialistsList ordering
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setSpecialists(data || []);
    } catch (err) {
      console.error('Error fetching specialists:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('trainers')
        .select('*')
        .eq('is_active', true)
        // Order by display_order (sequence set in admin) - matches AdminTrainersList ordering
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTrainers(data || []);
    } catch (err) {
      console.error('Error fetching trainers:', err);
    }
  };

  const filteredServices = activeFilter === 'All' 
    ? services 
    : activeFilter === 'Healing Session'
    ? services.filter(service => service.category === activeFilter)
    : activeFilter === 'Specialists' || activeFilter === 'Training Sessions'
    ? [] // Specialists and Training Sessions are handled separately
    : services.filter(service => service.type === activeFilter);
  
  const filteredSpecialists = activeFilter === 'Specialists' ? specialists : [];
  const filteredTrainers = activeFilter === 'Training Sessions' ? trainers : [];
  const activeFilterLabel = copy.filters[activeFilter] || activeFilter;
  
  const translateSpecialist = (specialist) => {
    if (!specialist) return specialist;
    
    const translated = { ...specialist };
    
    if (language === 'Arabic') {
      if (specialist.name_ar) translated.name = specialist.name_ar;
      if (specialist.title_ar) translated.title = specialist.title_ar;
      if (specialist.bio_ar) translated.bio = specialist.bio_ar;
      if (specialist.specialization_ar) translated.specialization = specialist.specialization_ar;
    } else if (language === 'Russian') {
      if (specialist.name_ru) translated.name = specialist.name_ru;
      if (specialist.title_ru) translated.title = specialist.title_ru;
      if (specialist.bio_ru) translated.bio = specialist.bio_ru;
      if (specialist.specialization_ru) translated.specialization = specialist.specialization_ru;
    } else if (language === 'German') {
      if (specialist.name_de) translated.name = specialist.name_de;
      if (specialist.title_de) translated.title = specialist.title_de;
      if (specialist.bio_de) translated.bio = specialist.bio_de;
      if (specialist.specialization_de) translated.specialization = specialist.specialization_de;
    } else if (language === 'French') {
      if (specialist.name_fr) translated.name = specialist.name_fr;
      if (specialist.title_fr) translated.title = specialist.title_fr;
      if (specialist.bio_fr) translated.bio = specialist.bio_fr;
      if (specialist.specialization_fr) translated.specialization = specialist.specialization_fr;
    }
    
    return translated;
  };
  
  const noResultsMessage = activeFilter === 'Specialists'
    ? (filteredSpecialists.length === 0 ? copy.noSpecialists : '')
    : activeFilter === 'Training Sessions'
    ? (filteredTrainers.length === 0 ? copy.noTrainingSessions : '')
    : copy.noResults(activeFilterLabel, copy.filters.All);

  return (
    <div className="services-page">
      {/* Header Section */}
      <section className="services-header">
        <div className="wave-header-bg"></div>
        <div className="container">
          <h1 className="services-page-title">{copy.title}</h1>
          <p className="services-subtitle">{copy.subtitle}</p>
        </div>
      </section>

      {/* Service Filters */}
      <section className="filters-section">
        <div className="container">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${activeFilter === 'All' ? 'active' : ''}`}
              onClick={() => setActiveFilter('All')}
            >
              {copy.filters.All}
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'Remote' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Remote')}
            >
              {copy.filters.Remote}
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'On-Call' ? 'active' : ''}`}
              onClick={() => setActiveFilter('On-Call')}
            >
              {copy.filters['On-Call']}
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'Healing Session' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Healing Session')}
            >
              {copy.filters['Healing Session']}
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'Specialists' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Specialists')}
            >
              {copy.filters['Specialists']}
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'Training Sessions' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Training Sessions')}
            >
              {copy.filters['Training Sessions']}
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid or Specialists Grid */}
      <section className="services-grid-section">
        <div className="container">
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p>{copy.loading}</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: '#ef4444' }}>{error}</p>
            </div>
          ) : activeFilter === 'Specialists' ? (
            filteredSpecialists.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <p>{noResultsMessage}</p>
              </div>
            ) : (
              <div className="services-grid">
                {filteredSpecialists.map((specialist) => {
                  const translatedSpecialist = translateSpecialist(specialist);
                  return (
                  <div key={specialist.id} className="service-card" onClick={() => navigate(`/specialists/${specialist.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="service-image-wrapper">
                      <img 
                        src={specialist.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'} 
                        alt={translatedSpecialist.name}
                        className="service-card-image"
                      />
                    </div>
                    <div className="service-card-content">
                      <h3 className="service-card-name">{translatedSpecialist.name}</h3>
                      {translatedSpecialist.title && (
                        <p style={{ color: '#C7D2FE', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                          {translatedSpecialist.title}
                        </p>
                      )}
                      {translatedSpecialist.specialization && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.specialization}</strong> {translatedSpecialist.specialization}
                        </p>
                      )}
                      {specialist.experience_years && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.experience}</strong> {specialist.experience_years} {copy.labels.years}
                        </p>
                      )}
                      {specialist.languages && Array.isArray(specialist.languages) && specialist.languages.length > 0 && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.languages}</strong> {specialist.languages.join(', ')}
                        </p>
                      )}
                      {translatedSpecialist.bio && (
                        <p className="service-description">{translatedSpecialist.bio.substring(0, 150)}...</p>
                      )}
                      <button 
                        className="btn-view-details"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/specialists/${specialist.id}`);
                        }}
                      >
                        {copy.viewDetails}
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )
          ) : activeFilter === 'Training Sessions' ? (
            filteredTrainers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <p>{noResultsMessage}</p>
              </div>
            ) : (
              <div className="services-grid">
                {filteredTrainers.map((trainer) => (
                  <div key={trainer.id} className="service-card">
                    <div className="service-image-wrapper">
                      <img 
                        src={trainer.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'} 
                        alt={trainer.name}
                        className="service-card-image"
                      />
                      <div className="service-type-badge">{copy.labels.trainer}</div>
                    </div>
                    <div className="service-card-content">
                      <h3 className="service-card-name">{trainer.name}</h3>
                      {trainer.title && (
                        <p style={{ color: '#C7D2FE', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                          {trainer.title}
                        </p>
                      )}
                      {trainer.specialization && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.specialization}</strong> {trainer.specialization}
                        </p>
                      )}
                      {trainer.certifications && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.certifications}</strong> {trainer.certifications}
                        </p>
                      )}
                      {trainer.experience_years && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.experience}</strong> {trainer.experience_years} {copy.labels.years}
                        </p>
                      )}
                      {trainer.languages && Array.isArray(trainer.languages) && trainer.languages.length > 0 && (
                        <p style={{ color: '#C7D2FE', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>{copy.labels.languages}</strong> {trainer.languages.join(', ')}
                        </p>
                      )}
                      {trainer.bio && (
                        <p className="service-description">{trainer.bio.substring(0, 150)}...</p>
                      )}
                      <button 
                        className="btn-view-details"
                        onClick={() => navigate('/trainings')}
                      >
                        {copy.viewDetails}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : filteredServices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p>{noResultsMessage}</p>
            </div>
          ) : (
            <div className="services-grid">
              {filteredServices.map((service) => {
                const translatedService = translateService(service, language);
                const typeLabel = typeTranslations[service.type]?.[language] || service.type;

                return (
                  <div key={service.id} className="service-card">
                    <div className="service-image-wrapper">
                      <img 
                          src={service.image || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&h=400&fit=crop&auto=format'} 
                        alt={translatedService.name}
                        className="service-card-image"
                      />
                        {service.has_audio && (
                        <div className="audio-indicator">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="white"/>
                            <path d="M16.5 12C16.5 10.23 15.48 8.71 14.5 7.97V16.03C15.48 15.29 16.5 13.77 16.5 12Z" fill="white"/>
                            <path d="M19 10V14C19 16.21 17.21 18 15 18H14.5V6H15C17.21 6 19 7.79 19 10Z" fill="white"/>
                          </svg>
                        </div>
                      )}
                      <div className="service-type-badge">
                        {typeLabel}
                      </div>
                    </div>
                    <div className="service-card-content">
                      <h3 className="service-card-name">{translatedService.name}</h3>
                      <div className="service-meta">
                        <span className="service-duration-badge">{translatedService.duration}</span>
                        <span className="service-price-badge">{formatPrice(translatedService.price)}</span>
                      </div>
                      <p className="service-description">{translatedService.description || service.description}</p>
                      <button 
                        className="btn-view-details"
                        onClick={() => navigate(`/services/${service.id}`)}
                      >
                        {copy.viewDetails}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer Section */}
      <footer className="services-footer">
        <div className="wave-footer-bg"></div>
        <div className="container">
          <div className="footer-content" style={{ textAlign: 'center' }}>
            <div className="footer-column">
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>Address: 1st Floor, 124 Cleveland Street, London W1T 6PG</p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="mailto:manawave@manawave.uk" style={{ color: '#C7D2FE', textDecoration: 'none' }}>manawave@manawave.uk</a>
              </p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="tel:+447356083797" style={{ color: '#C7D2FE', textDecoration: 'none' }}>+447356083797</a>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>Copyright © 2026 ManaWave LTD All Rights Reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Services;
